import {
  PRIVACY_EMAIL,
  COMPANY_LEGAL_NAME,
  BACKUP_PURGE_DAYS,
  DELETION_DAYS,
} from "./legalConfig";

/* Content for the public privacy policy and account-deletion pages.
   Each language has the same shape; the pages render whichever
   matches the app-wide LanguageContext. */

export const legalTranslations = {
  en: {
    dir: "ltr",
    common: {
      langLabel: "Language",
      backHome: "Back to Nexploy",
      lastUpdated: "Last updated",
      privacyLink: "Privacy Policy",
      deleteLink: "Delete account",
      contactLabel: "Privacy contact",
      rights: (y) => `© ${y} Nexploy. All rights reserved.`,
    },

    privacy: {
      docTitle: "Privacy Policy | Nexploy",
      title: "Privacy Policy",
      intro: [
        `This policy explains how ${COMPANY_LEGAL_NAME.en} ("Nexploy", "we") handles personal data in the Nexploy HRMS web platform and the Nexploy mobile app for Android and iOS.`,
        "Nexploy is a workplace tool. Your employer creates your account and decides which features are turned on. We process your data on behalf of your employer and according to its instructions. For most privacy requests, your employer's HR team is your first point of contact, and you can also reach us directly.",
      ],
      sections: [
        {
          id: "data",
          title: "Data we collect and why",
          lead: "We only collect what is needed to run the features your employer uses.",
          table: {
            head: ["Data", "Why we use it"],
            rows: [
              ["Name, work email, phone number, employee ID, job title and department", "To create and identify your account, show your profile to colleagues, and run HR workflows such as leave, payroll and approvals."],
              ["Precise location, only while you check in or out", "To confirm you are inside your office's geofence when you record attendance. We do not track your location in the background."],
              ["Profile photo and files you attach to documents or chats", "To show your photo in the app and to deliver the documents and attachments you share."],
              ["Face images, only if your employer enables face check-in and you consent in the app", "To verify that the person checking in is you. They are used for attendance verification only and for no other purpose."],
              ["Voice messages and chat messages", "To deliver workplace messages between you and your colleagues."],
              ["Device push token", "To send you notifications such as approvals, messages and reminders."],
              ["Crash diagnostics (optional)", "To find and fix app crashes. Reports are sent through Sentry and contain technical details such as device model, app version and error traces."],
            ],
          },
        },
        {
          id: "not-collected",
          title: "Data we do not collect",
          bullets: [
            "We do not access your contacts or SMS messages.",
            "We do not use advertising IDs and we do not show ads.",
            "We do not collect bank card or financial account numbers through the app.",
            "Fingerprint or Face ID unlock is handled entirely by your device. Your biometric data never leaves the phone and is never sent to us.",
          ],
        },
        {
          id: "employer",
          title: "Processing on behalf of your employer",
          paragraphs: [
            "Your employer is responsible for deciding what data is collected about you at work and why. We act as a service provider and process your data only to provide Nexploy to your employer. Authorized people at your employer, such as HR staff and your managers, can see data that relates to their role.",
          ],
        },
        {
          id: "sharing",
          title: "Sharing and selling",
          bullets: [
            "We do not sell your personal data, and we do not share it for advertising.",
            "We share data only with service providers that help us run Nexploy, such as hosting, push notification delivery (Apple and Google) and crash reporting (Sentry). They may use it only to provide their service to us.",
            "We may disclose data when the law requires it.",
          ],
        },
        {
          id: "security",
          title: "Security",
          bullets: [
            "All data between the app or browser and our servers is encrypted in transit using HTTPS (TLS).",
            "Access to personal data is limited by role and permission settings that your employer controls.",
            "Sign-in sessions expire after inactivity and can be revoked by your employer.",
          ],
        },
        {
          id: "retention",
          title: "How long we keep data",
          bullets: [
            "Account and HR data are kept while your employer uses Nexploy and your account is active.",
            "Face images are deleted when your employer turns face check-in off, when you withdraw consent, or when your account is deleted.",
            "Push tokens are removed when you sign out or your account is deleted.",
            `When an account is deleted, we delete its data within ${DELETION_DAYS} days. Copies in encrypted backups are overwritten within ${BACKUP_PURGE_DAYS} days.`,
            "Your employer may keep some records, such as attendance and payroll, for as long as labor and tax laws require.",
          ],
        },
        {
          id: "rights",
          title: "Your choices and rights",
          bullets: [
            "You can view and update much of your profile in the app.",
            "You can deny or withdraw location, camera and microphone permissions in your device settings. Some features, such as check-in, may then be unavailable.",
            "You can withdraw consent for face check-in at any time.",
            "You can ask for a copy, correction or deletion of your data through your employer or by contacting us.",
          ],
          link: { to: "/delete-account", label: "How to request account deletion" },
        },
        {
          id: "children",
          title: "Children",
          paragraphs: [
            "Nexploy is a workplace tool for employees and is not intended for children.",
          ],
        },
        {
          id: "changes",
          title: "Changes to this policy",
          paragraphs: [
            "We may update this policy. We will change the date at the top of this page and, for significant changes, let you know in the app.",
          ],
        },
        {
          id: "contact",
          title: "Contact us",
          paragraphs: [
            "For privacy questions or requests, email us. We usually reply within a few business days.",
          ],
          email: true,
        },
      ],
    },

    del: {
      docTitle: "Delete your account | Nexploy",
      title: "Request deletion of your Nexploy account and data",
      intro: [
        "Your Nexploy account was created by your employer, so you cannot delete it directly in the app. You can ask us to delete your account and personal data using the form below, or by emailing us. Uninstalling the app does not delete your data.",
      ],
      stepsTitle: "How to request deletion",
      steps: [
        "Fill in the form below, or send an email from your work email address with the same details.",
        "We confirm the request with you by replying to your work email, and we notify your employer.",
        `We delete your account and data within ${DELETION_DAYS} days of confirming the request and email you when it is done.`,
      ],
      employerTip: "You can also ask your employer's HR team to remove your account. They can deactivate it straight away.",
      formTitle: "Deletion request form",
      form: {
        name: "Full name",
        email: "Work email",
        company: "Company (employer)",
        employeeId: "Employee ID (optional)",
        scope: "What should we delete?",
        scopeAll: "My account and all my personal data",
        scopePart: "Only some of my data (describe below)",
        notes: "Details (optional)",
        submit: "Send deletion request",
        required: "Please fill in your name, a valid work email and your company.",
        sent: "Your email app should now be open with the request filled in. Send that email to complete your request.",
        fallback: "If no email app opened, email us directly at",
        mailSubject: "Account deletion request",
        mailBody: (f, scopeLabel) =>
          [
            "Please delete my Nexploy account and data.",
            "",
            `Full name: ${f.name}`,
            `Work email: ${f.email}`,
            `Company: ${f.company}`,
            `Employee ID: ${f.employeeId || "-"}`,
            `Request: ${scopeLabel}`,
            "",
            `Details: ${f.notes || "-"}`,
          ].join("\n"),
      },
      deletedTitle: "What we delete",
      deleted: [
        "Your sign-in account and profile: name, work email, phone number, employee ID, job title, department and profile photo.",
        "Face images used for face check-in.",
        "Your chat messages, voice messages and the attachments you uploaded.",
        "Location data collected during check-in, once it is no longer needed for retained attendance records.",
        "Device push tokens and crash diagnostics linked to your account.",
      ],
      retainedTitle: "What your employer may keep",
      retained: [
        "Your employer may be legally required to keep some employment records, such as attendance, payroll, salary and tax records, for a period set by labor and tax law.",
        "These records are kept only for that legal period, are not used for any other purpose, and are deleted when the period ends.",
      ],
      timeframeTitle: "Timeframe",
      timeframe: `We complete deletion within ${DELETION_DAYS} days of confirming your request. Copies in encrypted backups are overwritten within ${BACKUP_PURGE_DAYS} days.`,
      emailTitle: "Prefer email?",
      emailText: "Send your full name, company and the details above from your work email to",
    },
  },

  ar: {
    dir: "rtl",
    common: {
      langLabel: "اللغة",
      backHome: "العودة إلى Nexploy",
      lastUpdated: "آخر تحديث",
      privacyLink: "سياسة الخصوصية",
      deleteLink: "حذف الحساب",
      contactLabel: "التواصل بشأن الخصوصية",
      rights: (y) => `© ${y} Nexploy. جميع الحقوق محفوظة.`,
    },

    privacy: {
      docTitle: "سياسة الخصوصية | Nexploy",
      title: "سياسة الخصوصية",
      intro: [
        `توضح هذه السياسة كيف تتعامل ${COMPANY_LEGAL_NAME.ar} ("Nexploy" أو "نحن") مع البيانات الشخصية في منصة Nexploy لإدارة الموارد البشرية على الويب وفي تطبيق Nexploy للهاتف على أندرويد وiOS.`,
        "Nexploy أداة عمل. صاحب العمل هو من ينشئ حسابك ويحدد المزايا المفعّلة. نحن نعالج بياناتك نيابةً عن صاحب العمل ووفقًا لتعليماته. في معظم طلبات الخصوصية يكون فريق الموارد البشرية لدى صاحب العمل هو جهة التواصل الأولى، ويمكنك أيضًا التواصل معنا مباشرة.",
      ],
      sections: [
        {
          id: "data",
          title: "البيانات التي نجمعها وسبب جمعها",
          lead: "نجمع فقط ما يلزم لتشغيل المزايا التي يستخدمها صاحب العمل.",
          table: {
            head: ["البيانات", "سبب استخدامها"],
            rows: [
              ["الاسم والبريد الإلكتروني للعمل ورقم الهاتف والرقم الوظيفي والمسمى الوظيفي والقسم", "لإنشاء حسابك والتعريف به، وعرض ملفك لزملائك، وتشغيل إجراءات الموارد البشرية مثل الإجازات والرواتب والموافقات."],
              ["الموقع الدقيق، أثناء تسجيل الحضور أو الانصراف فقط", "للتأكد من وجودك داخل النطاق الجغرافي لمكتبك عند تسجيل الحضور. لا نتتبع موقعك في الخلفية."],
              ["الصورة الشخصية والملفات التي ترفقها بالمستندات أو المحادثات", "لعرض صورتك في التطبيق وتوصيل المستندات والمرفقات التي تشاركها."],
              ["صور الوجه، فقط إذا فعّل صاحب العمل تسجيل الحضور بالوجه ووافقت على ذلك داخل التطبيق", "للتحقق من أن الشخص الذي يسجل الحضور هو أنت. تُستخدم للتحقق من الحضور فقط ولا تُستخدم لأي غرض آخر."],
              ["الرسائل الصوتية ورسائل المحادثة", "لتوصيل رسائل العمل بينك وبين زملائك."],
              ["رمز الإشعارات الخاص بالجهاز", "لإرسال الإشعارات إليك مثل الموافقات والرسائل والتذكيرات."],
              ["بيانات تشخيص الأعطال (اختيارية)", "لاكتشاف أعطال التطبيق وإصلاحها. تُرسل التقارير عبر خدمة Sentry وتتضمن تفاصيل تقنية مثل طراز الجهاز وإصدار التطبيق وتفاصيل الخطأ."],
            ],
          },
        },
        {
          id: "not-collected",
          title: "البيانات التي لا نجمعها",
          bullets: [
            "لا نصل إلى جهات الاتصال أو الرسائل النصية القصيرة.",
            "لا نستخدم معرّفات الإعلانات ولا نعرض أي إعلانات.",
            "لا نجمع أرقام البطاقات البنكية أو الحسابات المالية عبر التطبيق.",
            "فتح القفل ببصمة الإصبع أو بصمة الوجه يتم بالكامل على جهازك. بياناتك الحيوية لا تغادر الهاتف ولا تُرسل إلينا أبدًا.",
          ],
        },
        {
          id: "employer",
          title: "المعالجة نيابةً عن صاحب العمل",
          paragraphs: [
            "صاحب العمل هو المسؤول عن تحديد البيانات التي تُجمع عنك في العمل وسبب جمعها. نحن نعمل كمقدّم خدمة ونعالج بياناتك فقط لتقديم Nexploy لصاحب العمل. يمكن للأشخاص المصرّح لهم لدى صاحب العمل، مثل موظفي الموارد البشرية ومديريك، رؤية البيانات المتعلقة بدورهم.",
          ],
        },
        {
          id: "sharing",
          title: "المشاركة والبيع",
          bullets: [
            "لا نبيع بياناتك الشخصية، ولا نشاركها لأغراض إعلانية.",
            "نشارك البيانات فقط مع مقدّمي الخدمات الذين يساعدوننا في تشغيل Nexploy، مثل الاستضافة وتوصيل الإشعارات (Apple وGoogle) وتقارير الأعطال (Sentry). ولا يحق لهم استخدامها إلا لتقديم خدمتهم لنا.",
            "قد نفصح عن البيانات عندما يفرض القانون ذلك.",
          ],
        },
        {
          id: "security",
          title: "الأمان",
          bullets: [
            "جميع البيانات المتبادلة بين التطبيق أو المتصفح وخوادمنا مشفّرة أثناء النقل باستخدام HTTPS (TLS).",
            "الوصول إلى البيانات الشخصية محدود بإعدادات الأدوار والصلاحيات التي يتحكم فيها صاحب العمل.",
            "تنتهي جلسات تسجيل الدخول بعد فترة من عدم النشاط، ويمكن لصاحب العمل إلغاؤها.",
          ],
        },
        {
          id: "retention",
          title: "مدة الاحتفاظ بالبيانات",
          bullets: [
            "نحتفظ ببيانات الحساب والموارد البشرية طالما يستخدم صاحب العمل Nexploy وكان حسابك نشطًا.",
            "تُحذف صور الوجه عندما يوقف صاحب العمل تسجيل الحضور بالوجه، أو عندما تسحب موافقتك، أو عند حذف حسابك.",
            "تُزال رموز الإشعارات عند تسجيل الخروج أو حذف الحساب.",
            `عند حذف الحساب نحذف بياناته خلال ${DELETION_DAYS} يومًا. وتُستبدل النسخ الموجودة في النسخ الاحتياطية المشفّرة خلال ${BACKUP_PURGE_DAYS} يومًا.`,
            "قد يحتفظ صاحب العمل ببعض السجلات، مثل الحضور والرواتب، طوال المدة التي تفرضها قوانين العمل والضرائب.",
          ],
        },
        {
          id: "rights",
          title: "خياراتك وحقوقك",
          bullets: [
            "يمكنك عرض جزء كبير من بيانات ملفك الشخصي وتحديثه داخل التطبيق.",
            "يمكنك رفض أذونات الموقع والكاميرا والميكروفون أو سحبها من إعدادات جهازك. وقد تصبح بعض المزايا، مثل تسجيل الحضور، غير متاحة عندئذٍ.",
            "يمكنك سحب موافقتك على تسجيل الحضور بالوجه في أي وقت.",
            "يمكنك طلب نسخة من بياناتك أو تصحيحها أو حذفها عن طريق صاحب العمل أو بالتواصل معنا.",
          ],
          link: { to: "/delete-account", label: "طريقة طلب حذف الحساب" },
        },
        {
          id: "children",
          title: "الأطفال",
          paragraphs: [
            "Nexploy أداة عمل مخصصة للموظفين وليست موجّهة للأطفال.",
          ],
        },
        {
          id: "changes",
          title: "التغييرات على هذه السياسة",
          paragraphs: [
            "قد نحدّث هذه السياسة. سنغيّر التاريخ أعلى هذه الصفحة، وسنبلغك داخل التطبيق عند إجراء تغييرات جوهرية.",
          ],
        },
        {
          id: "contact",
          title: "تواصل معنا",
          paragraphs: [
            "لأسئلة الخصوصية أو الطلبات المتعلقة بها، راسلنا عبر البريد الإلكتروني. نرد عادةً خلال أيام عمل قليلة.",
          ],
          email: true,
        },
      ],
    },

    del: {
      docTitle: "حذف الحساب | Nexploy",
      title: "طلب حذف حسابك وبياناتك في Nexploy",
      intro: [
        "أنشأ صاحب العمل حسابك في Nexploy، لذلك لا يمكنك حذفه مباشرة من داخل التطبيق. يمكنك أن تطلب منا حذف حسابك وبياناتك الشخصية باستخدام النموذج التالي أو بمراسلتنا عبر البريد الإلكتروني. حذف التطبيق من هاتفك لا يحذف بياناتك.",
      ],
      stepsTitle: "طريقة طلب الحذف",
      steps: [
        "املأ النموذج التالي، أو أرسل بريدًا إلكترونيًا من بريد العمل الخاص بك يتضمن البيانات نفسها.",
        "نؤكد الطلب معك بالرد على بريد العمل الخاص بك، ونُبلغ صاحب العمل.",
        `نحذف حسابك وبياناتك خلال ${DELETION_DAYS} يومًا من تأكيد الطلب، ونراسلك عند الانتهاء.`,
      ],
      employerTip: "يمكنك أيضًا أن تطلب من فريق الموارد البشرية لدى صاحب العمل إزالة حسابك، إذ يمكنهم إيقافه فورًا.",
      formTitle: "نموذج طلب الحذف",
      form: {
        name: "الاسم بالكامل",
        email: "بريد العمل الإلكتروني",
        company: "الشركة (صاحب العمل)",
        employeeId: "الرقم الوظيفي (اختياري)",
        scope: "ما الذي تريد حذفه؟",
        scopeAll: "حسابي وجميع بياناتي الشخصية",
        scopePart: "جزء من بياناتي فقط (وضّح بالأسفل)",
        notes: "تفاصيل (اختياري)",
        submit: "إرسال طلب الحذف",
        required: "يرجى إدخال الاسم وبريد عمل صحيح واسم الشركة.",
        sent: "من المفترض أن يكون تطبيق البريد قد فُتح الآن والطلب مكتوب فيه. أرسل هذه الرسالة لإكمال طلبك.",
        fallback: "إذا لم يُفتح تطبيق البريد، راسلنا مباشرة على",
        mailSubject: "طلب حذف حساب",
        mailBody: (f, scopeLabel) =>
          [
            "أرجو حذف حسابي وبياناتي في Nexploy.",
            "",
            `الاسم بالكامل: ${f.name}`,
            `بريد العمل: ${f.email}`,
            `الشركة: ${f.company}`,
            `الرقم الوظيفي: ${f.employeeId || "-"}`,
            `الطلب: ${scopeLabel}`,
            "",
            `تفاصيل: ${f.notes || "-"}`,
          ].join("\n"),
      },
      deletedTitle: "ما الذي نحذفه",
      deleted: [
        "حساب تسجيل الدخول وملفك الشخصي: الاسم وبريد العمل ورقم الهاتف والرقم الوظيفي والمسمى الوظيفي والقسم والصورة الشخصية.",
        "صور الوجه المستخدمة في تسجيل الحضور بالوجه.",
        "رسائل المحادثة والرسائل الصوتية والمرفقات التي رفعتها.",
        "بيانات الموقع التي جُمعت أثناء تسجيل الحضور، عندما لا تعود لازمة لسجلات الحضور المحتفظ بها.",
        "رموز الإشعارات وبيانات تشخيص الأعطال المرتبطة بحسابك.",
      ],
      retainedTitle: "ما قد يحتفظ به صاحب العمل",
      retained: [
        "قد يكون صاحب العمل ملزمًا قانونًا بالاحتفاظ ببعض سجلات التوظيف، مثل سجلات الحضور والرواتب والأجور والضرائب، لمدة تحددها قوانين العمل والضرائب.",
        "تُحفظ هذه السجلات طوال تلك المدة القانونية فقط، ولا تُستخدم لأي غرض آخر، وتُحذف عند انتهائها.",
      ],
      timeframeTitle: "المدة الزمنية",
      timeframe: `ننتهي من الحذف خلال ${DELETION_DAYS} يومًا من تأكيد طلبك. وتُستبدل النسخ الموجودة في النسخ الاحتياطية المشفّرة خلال ${BACKUP_PURGE_DAYS} يومًا.`,
      emailTitle: "تفضّل البريد الإلكتروني؟",
      emailText: "أرسل اسمك بالكامل واسم الشركة والتفاصيل السابقة من بريد العمل الخاص بك إلى",
    },
  },
};

export { PRIVACY_EMAIL };
